import React from 'react';
import { motion } from 'framer-motion';
import { PillCelebrationEffect } from '../../../components/Celebration/PillCelebrationEffect';
import { PillSadnessEffect } from '../../../components/Celebration/PillSadnessEffect';
import { CelebrationPillProps } from '../../../presentation/GamePresentationTypes';
import { arenaAudio } from '../../../services/ArenaAudioService';
import { getTeamForSeat } from '../../../engine/gameConfigHelpers';

const YOU_GOT_SET_SAYINGS = [
  'You Got Set',
  'Set!',
  'We Got Set',
  'Took The Set',
  'Down We Go',
  'Ouch, Set!',
  'Fell Short',
  'Got Burned',
  'Down In Flames',
  'Took The Hit',
];

const OPPONENTS_MADE_BID_SAYINGS = [
  'They Made It',
  'Opponents Made Bid',
  'They Got Theirs',
  'Contract Made',
  'Books Allowed',
  'They Took It',
  'They Scored',
];

const YOU_SET_THEM_SAYINGS = [
  'You Set Them!',
  'They Got Set!',
  "Set 'Em!",
  'Denied!',
  'Shut Down!',
  'Not Today!',
  "Caught Slippin'!",
  'Total Denial!',
  "Got 'Em Set!",
];

const BID_MADE_SAYINGS = [
  'Bid Made!',
  'Made The Bid!',
  'Books Secured!',
  'In The Bag!',
  'Nailed It!',
  "Count 'Em Up!",
  'Contract Made!',
  'Cash It In!',
  'Made It!',
];

const RANDOM_BOSTON_WIN_SAYINGS = [
  'Boston...sweeet!',
  'Boston Clean Sweep!',
  'Boston Domination!',
  'Total Boston Run!',
  'Boston Flawless!',
  'All 12 In The Books!',
  'Running The Table!'
];

const getRandomItem = (list: string[], seed: number, salt: number): string => {
  if (!list.length) return '';
  const pseudo = Math.abs(Math.sin(seed * 10000 + salt * 79.19));
  const idx = Math.floor(pseudo * list.length) % list.length;
  return list[idx];
};

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className = 'text-gray-300/80' }) => (
  <motion.span
    animate={{ x: [0, 4, 0] }}
    transition={{
      duration: 1.2,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
    className="inline-flex items-center justify-center shrink-0"
  >
    <svg
      className={`w-5 h-5 sm:w-6 sm:h-6 ${className} transition-all shrink-0 select-none pointer-events-none`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  </motion.span>
);

export const BidWhistCelebrationPill: React.FC<CelebrationPillProps> = ({
  gameState,
  onDismiss,
  bostonResult,
  isBiddingTeamSet,
  isUserTeamSet,
  isClaimRevealActive,
  isUserTeamBidder,
}) => {
  const [randomSeed] = React.useState<number>(() => Math.random());

  const youGotSetTitle = React.useMemo(() => getRandomItem(YOU_GOT_SET_SAYINGS, randomSeed, 1), [randomSeed]);
  const opponentsMadeBidTitle = React.useMemo(() => getRandomItem(OPPONENTS_MADE_BID_SAYINGS, randomSeed, 2), [randomSeed]);
  const youSetThemTitle = React.useMemo(() => getRandomItem(YOU_SET_THEM_SAYINGS, randomSeed, 3), [randomSeed]);
  const bidMadeTitle = React.useMemo(() => getRandomItem(BID_MADE_SAYINGS, randomSeed, 4), [randomSeed]);
  const bostonWinTitle = React.useMemo(() => getRandomItem(RANDOM_BOSTON_WIN_SAYINGS, randomSeed, 5), [randomSeed]);

  const playedAudioRef = React.useRef(false);
  React.useEffect(() => {
    if (playedAudioRef.current) return;
    playedAudioRef.current = true;
    try {
      if (bostonResult) {
        if (bostonResult.isUserTeam) {
          arenaAudio.playBostonPrizeTone();
        } else {
          arenaAudio.playDescending5ToneSet();
        }
      } else if (isBiddingTeamSet) {
        if (isUserTeamSet === false) {
          arenaAudio.playAscending5ToneSuccess();
        } else {
          arenaAudio.playDescending5ToneSet();
        }
      } else {
        if (isClaimRevealActive || Boolean(gameState.isClaimed)) {
          arenaAudio.playAscending5ToneSuccess();
        } else if (isUserTeamBidder === false) {
          arenaAudio.playDescending5ToneSet();
        } else {
          arenaAudio.playAscending5ToneSuccess();
        }
      }
    } catch {}
  }, [bostonResult, isBiddingTeamSet, isUserTeamSet, isClaimRevealActive, gameState.isClaimed, isUserTeamBidder]);

  return (
    <div className="absolute bottom-8 sm:bottom-11 md:bottom-14 left-1/2 -translate-x-1/2 z-50 pointer-events-auto select-none max-w-[90vw]">
      {bostonResult ? (
        bostonResult.isUserTeam ? (
          <div className="relative">
            <PillCelebrationEffect />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 hover:from-amber-400 hover:via-amber-500 hover:to-orange-500 shadow-[0_10px_30px_rgba(245,158,11,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2.5 sm:gap-3">
                <span className="text-base sm:text-xl leading-none">🏆</span>
                <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]">
                  {bostonWinTitle || bostonResult.title}
                </span>
              </div>
              <ChevronRightIcon className="text-amber-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        ) : (
          <div className="relative">
            <PillSadnessEffect variant="set" />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-red-500 via-rose-600 to-red-700 hover:from-red-400 hover:via-rose-500 hover:to-red-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2.5 sm:gap-3">
                <span className="text-base sm:text-xl leading-none">💥</span>
                <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                  {bostonResult.title}
                </span>
              </div>
              <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        )
      ) : isBiddingTeamSet ? (
        isUserTeamSet === false ? (
          <div className="relative">
            <PillCelebrationEffect />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.75 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-700 hover:from-emerald-400 hover:via-emerald-500 hover:to-teal-600 shadow-[0_10px_30px_rgba(16,185,129,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2.5 sm:gap-3">
                <span className="text-base sm:text-xl leading-none">💥</span>
                <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                  {youSetThemTitle}
                </span>
              </div>
              <ChevronRightIcon className="text-emerald-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        ) : (
          <div className="relative">
            <PillSadnessEffect variant="set" />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-red-500 via-rose-600 to-red-700 hover:from-red-400 hover:via-rose-500 hover:to-red-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2.5 sm:gap-3">
                <span className="text-base sm:text-xl leading-none">💥</span>
                <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                  {isUserTeamSet === null
                    ? (gameState.winningBidderSeat !== null && gameState.winningBidderSeat !== undefined
                        ? `Team ${getTeamForSeat(gameState.winningBidderSeat)} Got Set!`
                        : 'Bidding Team Got Set!')
                    : youGotSetTitle}
                </span>
              </div>
              <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        )
      ) : (
        // Hand was NOT set (Bid made or claimed)
        (isClaimRevealActive || Boolean(gameState.isClaimed)) ? (
          <div className="relative">
            <PillCelebrationEffect />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.75 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 hover:from-amber-400 hover:via-amber-500 hover:to-orange-500 shadow-[0_10px_30px_rgba(245,158,11,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white cursor-pointer transition-all active:scale-95 min-w-0"
            >
              <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 shrink">
                <span className="text-base sm:text-xl leading-none shrink-0">🏆</span>
                <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)] truncate whitespace-nowrap">
                  Tricks Claimed
                </span>
              </div>
              <ChevronRightIcon className="text-amber-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        ) : isUserTeamBidder === false ? (
          <div className="relative">
            <PillSadnessEffect variant="opponents_bid" />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-rose-500 via-pink-600 to-rose-700 hover:from-rose-400 hover:via-pink-500 hover:to-rose-600 shadow-[0_10px_30px_rgba(244,63,94,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2.5 sm:gap-3">
                <span className="text-base sm:text-xl leading-none">🌧️</span>
                <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                  {opponentsMadeBidTitle}
                </span>
              </div>
              <ChevronRightIcon className="text-rose-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        ) : (
          <div className="relative">
            <PillCelebrationEffect />
            <motion.button
              type="button"
              onClick={onDismiss}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              initial={{ opacity: 0, scale: 0.75 }}
              animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
              transition={{
                y: { repeat: Infinity, duration: 1.1, ease: 'easeInOut' },
                opacity: { duration: 0.25 },
                scale: { duration: 0.25 },
              }}
              aria-label="Next"
              className="relative z-20 group flex items-center justify-between gap-4 sm:gap-6 px-6 sm:px-8 py-3.5 sm:py-4.5 rounded-full bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 hover:from-amber-400 hover:via-amber-500 hover:to-orange-500 shadow-[0_10px_30px_rgba(245,158,11,0.45),0_2px_10px_rgba(0,0,0,0.4)] backdrop-blur-xl text-white whitespace-nowrap cursor-pointer transition-all active:scale-95"
            >
              <div className="flex items-center gap-2.5 sm:gap-3">
                <span className="text-base sm:text-xl leading-none">🏆</span>
                <span className="text-sm sm:text-base font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]">
                  {isUserTeamBidder === true
                    ? bidMadeTitle
                    : (gameState.winningBidderSeat !== null && gameState.winningBidderSeat !== undefined
                        ? `Team ${getTeamForSeat(gameState.winningBidderSeat)} Made Their Bid!`
                        : 'Bid Made!')}
                </span>
              </div>
              <ChevronRightIcon className="text-amber-100/90 group-hover:text-white group-hover:translate-x-0.5" />
            </motion.button>
          </div>
        )
      )}
    </div>
  );
};
